import React, { useState } from 'react';
import { observer } from 'mobx-react';
import MeetingStatusBadge from './MeetingStatusBadge';
import MeetingDetail from './MeetingDetail';
import {
  Card, CardHeader, Chevron, CardSummary, CardTitleRow, CardTitle, CardMeta,
  MetaItem, MetaSeparator, CardActions, SalesPersonChip
} from '../../pages/MyMeetingsPage.styles';

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 0) {
    const futureMin = Math.round(-diff / 60_000);
    if (futureMin < 60) return `in ${futureMin} min`;
    const futureHr = Math.round(-diff / 3_600_000);
    if (futureHr < 24) return `in ${futureHr} hr${futureHr === 1 ? '' : 's'}`;
    const futureDay = Math.round(-diff / 86_400_000);
    return `in ${futureDay} day${futureDay === 1 ? '' : 's'}`;
  }
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(diff / 3_600_000);
  if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(diff / 86_400_000);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const month = Math.round(day / 30);
  return `${month} month${month === 1 ? '' : 's'} ago`;
};

const formatAbsolute = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  };
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const summarizeParticipants = (participants, validatedParticipants) => {
  const list = (participants && participants.length > 0)
    ? participants
    : (validatedParticipants || []).map(email => ({ email, name: email }));

  if (!list || list.length === 0) return null;

  const names = list.map(p => p.name || p.email).filter(Boolean);
  if (names.length === 0) return null;
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
};

const MeetingCard = observer(({
  meeting,
  myMeetingsStore,
  companyStore,
  usersStore,
  scope,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const duration = formatDuration(meeting.duration);
  const relative = formatRelative(meeting.meetingDate);
  const absolute = formatAbsolute(meeting.meetingDate);

  // Build meta in scannable order: when (date + time), how long ago, how long it ran, who attended.
  // Empty fields are skipped so we don't render stranded separators.
  const metaParts = [
    absolute?.date,
    absolute?.time,
    relative,
    duration,
    summarizeParticipants(meeting.participants, meeting.validatedParticipants)
  ].filter(Boolean);

  return (
    <Card>
      <CardHeader onClick={() => setExpanded(e => !e)}>
        <Chevron $expanded={expanded}>{'▶'}</Chevron>
        <CardSummary>
          <CardTitleRow>
            <CardTitle>{meeting.meetingTitle || 'Untitled Meeting'}</CardTitle>
            {scope === 'team' && meeting.salesPersonName && (
              <SalesPersonChip>{meeting.salesPersonName}</SalesPersonChip>
            )}
          </CardTitleRow>
          <CardMeta>
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <MetaSeparator>·</MetaSeparator>}
                <MetaItem>{part}</MetaItem>
              </React.Fragment>
            ))}
          </CardMeta>
        </CardSummary>
        <CardActions>
          <MeetingStatusBadge status={meeting.status} />
        </CardActions>
      </CardHeader>

      {expanded && (
        <MeetingDetail
          meeting={meeting}
          myMeetingsStore={myMeetingsStore}
          companyStore={companyStore}
          usersStore={usersStore}
          scope={scope}
        />
      )}
    </Card>
  );
});

export default MeetingCard;
